import { cn } from "@/lib/utils";

/**
 * Stylized geometric crests for Primera División clubs and any other
 * club we want to display. Pure SVG, deterministic, no network.
 */
type CrestSpec = {
  primary: string;
  secondary: string;
  accent?: string;
  initials: string;
  pattern?: "stripes-v" | "stripes-h" | "split" | "diagonal" | "solid";
};

const CRESTS: Record<string, CrestSpec> = {
  river: { primary: "#ffffff", secondary: "#e30613", initials: "CARP", pattern: "diagonal" },
  "river plate": { primary: "#ffffff", secondary: "#e30613", initials: "CARP", pattern: "diagonal" },
  boca: { primary: "#0a3a82", secondary: "#fcd116", initials: "CABJ", pattern: "stripes-h" },
  "boca juniors": { primary: "#0a3a82", secondary: "#fcd116", initials: "CABJ", pattern: "stripes-h" },
  racing: { primary: "#62b3e3", secondary: "#ffffff", initials: "RC", pattern: "stripes-v" },
  independiente: { primary: "#d51d27", secondary: "#ffffff", initials: "CAI", pattern: "solid" },
  "san lorenzo": { primary: "#0a2a5e", secondary: "#9b1d1d", initials: "CASLA", pattern: "stripes-v" },
  estudiantes: { primary: "#ffffff", secondary: "#d51d27", initials: "EDLP", pattern: "stripes-h" },
  velez: { primary: "#ffffff", secondary: "#0c3b91", initials: "CAVSF", pattern: "diagonal" },
  "vélez": { primary: "#ffffff", secondary: "#0c3b91", initials: "CAVSF", pattern: "diagonal" },
  lanus: { primary: "#7c1a1f", secondary: "#ffffff", initials: "CAL", pattern: "solid" },
  "lanús": { primary: "#7c1a1f", secondary: "#ffffff", initials: "CAL", pattern: "solid" },
  newells: { primary: "#d51d27", secondary: "#000000", initials: "CANOB", pattern: "split" },
  "newell's old boys": { primary: "#d51d27", secondary: "#000000", initials: "CANOB", pattern: "split" },
  rosario: { primary: "#0c3b91", secondary: "#fcd116", initials: "RC", pattern: "stripes-v" },
  "rosario central": { primary: "#0c3b91", secondary: "#fcd116", initials: "RC", pattern: "stripes-v" },
  huracan: { primary: "#ffffff", secondary: "#d51d27", initials: "CAH", pattern: "solid" },
  "huracán": { primary: "#ffffff", secondary: "#d51d27", initials: "CAH", pattern: "solid" },
  talleres: { primary: "#1a4ea2", secondary: "#ffffff", initials: "CAT", pattern: "stripes-h" },
  defensa: { primary: "#fcd116", secondary: "#13803a", initials: "DyJ", pattern: "split" },
  "defensa y justicia": { primary: "#fcd116", secondary: "#13803a", initials: "DyJ", pattern: "split" },
  argentinos: { primary: "#d51d27", secondary: "#ffffff", initials: "AAAJ", pattern: "split" },
  "argentinos juniors": { primary: "#d51d27", secondary: "#ffffff", initials: "AAAJ", pattern: "split" },
  godoy: { primary: "#0c3b91", secondary: "#ffffff", initials: "GC", pattern: "stripes-v" },
  "godoy cruz": { primary: "#0c3b91", secondary: "#ffffff", initials: "GC", pattern: "stripes-v" },
  banfield: { primary: "#13803a", secondary: "#ffffff", initials: "CAB", pattern: "stripes-v" },
  platense: { primary: "#7a3a18", secondary: "#ffffff", initials: "CAP", pattern: "stripes-v" },
  union: { primary: "#d51d27", secondary: "#ffffff", initials: "CAU", pattern: "split" },
  "unión": { primary: "#d51d27", secondary: "#ffffff", initials: "CAU", pattern: "split" },
  colon: { primary: "#000000", secondary: "#d51d27", initials: "CAC", pattern: "stripes-v" },
  "colón": { primary: "#000000", secondary: "#d51d27", initials: "CAC", pattern: "stripes-v" },
};

function slug(name: string) {
  return name.toLowerCase().trim();
}

export function Crest({ team, className }: { team: string; className?: string }) {
  const k = slug(team);
  const spec: CrestSpec =
    CRESTS[k] ?? {
      primary: "#0c3b91",
      secondary: "#7cc4ec",
      initials: team
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 3)
        .toUpperCase(),
      pattern: "split",
    };

  return (
    <svg
      viewBox="0 0 40 48"
      className={cn("h-8 w-7 shrink-0 drop-shadow-sm", className)}
      role="img"
      aria-label={`${team} crest`}
    >
      <defs>
        <clipPath id={`shield-${k.replace(/\s+/g, "")}`}>
          <path d="M20 1 L38 6 V26 C38 38 30 45 20 47 C10 45 2 38 2 26 V6 Z" />
        </clipPath>
      </defs>
      <g clipPath={`url(#shield-${k.replace(/\s+/g, "")})`}>
        <rect width="40" height="48" fill={spec.primary} />
        {spec.pattern === "stripes-v" && (
          <g fill={spec.secondary}>
            <rect x="8" width="6" height="48" />
            <rect x="26" width="6" height="48" />
          </g>
        )}
        {spec.pattern === "stripes-h" && (
          <rect y="18" width="40" height="10" fill={spec.secondary} />
        )}
        {spec.pattern === "split" && (
          <rect width="20" height="48" fill={spec.secondary} />
        )}
        {spec.pattern === "diagonal" && (
          <polygon points="0,48 48,0 48,12 12,48" fill={spec.secondary} />
        )}
      </g>
      <path
        d="M20 1 L38 6 V26 C38 38 30 45 20 47 C10 45 2 38 2 26 V6 Z"
        fill="none"
        stroke="#0a1a3d"
        strokeOpacity="0.4"
        strokeWidth="1"
      />
      <text
        x="20"
        y="30"
        textAnchor="middle"
        fontFamily="'Bebas Neue', 'Barlow Condensed', sans-serif"
        fontSize="9"
        fill="#0a1a3d"
        stroke="#fff"
        strokeWidth="0.4"
        paintOrder="stroke"
      >
        {spec.initials}
      </text>
    </svg>
  );
}
